import type { DeskDetail } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import { Plug, Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { Section } from '@/components/section';
import { ConnectionStatusBadge } from '@/components/status-badge';
import { EmptyState } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useApiMutation, useConnections } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { CONNECTION_TYPE, PERMISSION_LABEL } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

/** Desk connection permissions with `write` switched on or off; `read` is always kept, other values are preserved. */
function withWrite(permissions: string[], write: boolean): string[] {
  const other = permissions.filter((p) => p !== 'read' && p !== 'write');
  return ['read', ...(write ? ['write'] : []), ...other];
}

export function DeskConnectionsSection({ desk }: { desk: DeskDetail }) {
  const setPermissions = useApiMutation(
    (v: { connectionId: string; permissions: string[] }) => api.desks.addConnection(desk.id, v.connectionId, v.permissions),
    {
      invalidate: (_d, v) => [qk.desk(desk.id), qk.connection(v.connectionId)],
      success: (_d, v) => (v.permissions.includes('write') ? t('Write allowed') : t('Read only from now on')),
    },
  );
  const remove = useApiMutation((connectionId: string) => api.desks.removeConnection(desk.id, connectionId), {
    invalidate: (_d, id) => [qk.desk(desk.id), qk.connection(id)],
    success: t('Connection removed'),
  });

  return (
    <Section
      icon={<Plug />}
      title={t('Connections')}
      description={t('External systems that Agents on this desk are allowed to use.')}
      actions={
        <AddConnectionDialog desk={desk}>
          <Button variant="outline" size="sm">
            <Plus /> {t('Add')}
          </Button>
        </AddConnectionDialog>
      }
    >
      {desk.connections.length === 0 ? (
        <EmptyState compact title={t('No Connections yet')} description={t('Agents can’t reach any external systems here yet.')} />
      ) : (
        <>
          <ul className="-mx-5 divide-y border-y">
            {desk.connections.map((c) => (
              <li key={c.id} className="flex flex-col gap-1.5 px-5 py-2.5">
                <div className="flex items-center gap-2">
                  <Link
                    to="/w/$workspaceId/connections/$connectionId"
                    params={{ workspaceId: desk.workspaceId, connectionId: c.id }}
                    className="min-w-0 flex-1 truncate text-sm font-medium hover:text-primary hover:underline"
                  >
                    {c.name}
                  </Link>
                  <ConnectionStatusBadge status={c.status} />
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="-mr-2 size-7"
                    aria-label={t('Remove from desk')}
                    title={t('Remove from desk')}
                    onClick={() => remove.mutate(c.id)}
                    disabled={remove.isPending}
                  >
                    <X />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{CONNECTION_TYPE[c.type]?.label ?? c.type}</span>
                    {c.permissions.length === 0 && <span className="text-amber-700 dark:text-amber-400">{t('no permissions')}</span>}
                    {c.permissions.map((p) => (
                      <Badge key={p} tone={p === 'write' ? 'amber' : 'outline'} className="px-1.5 py-0 text-[11px]">
                        {PERMISSION_LABEL[p] ?? p}
                      </Badge>
                    ))}
                  </div>
                  <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground" title={t('Allow tools and actions that make changes')}>
                    {t('Write')}
                    <Switch
                      checked={c.permissions.includes('write')}
                      disabled={setPermissions.isPending}
                      onCheckedChange={(on) => setPermissions.mutate({ connectionId: c.id, permissions: withWrite(c.permissions, on) })}
                      aria-label={t('Allow Write for {name}', { name: c.name })}
                    />
                  </label>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">{t('Without Write, Agents can only use read-only tools.')}</p>
        </>
      )}
    </Section>
  );
}

function AddConnectionDialog({ desk, children }: { desk: DeskDetail; children: ReactNode }) {
  const connections = useConnections(desk.workspaceId);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState('');
  const [write, setWrite] = useState(false);
  const linked = new Set(desk.connections.map((c) => c.id));
  const addable = (connections.data ?? []).filter((c) => !linked.has(c.id));

  const add = useApiMutation(() => api.desks.addConnection(desk.id, pick, write ? ['read', 'write'] : ['read']), {
    invalidate: [qk.desk(desk.id), qk.connection(pick)],
    success: t('Connection added'),
    onSuccess: () => {
      setOpen(false);
      setPick('');
      setWrite(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Add Connection')}</DialogTitle>
          <DialogDescription>{t('Choose a Connection from this Workspace or one shared with it. You create new Connections under “Connections”.')}</DialogDescription>
        </DialogHeader>
        {connections.isSuccess && addable.length === 0 ? (
          <EmptyState
            compact
            title={t('No more Connections')}
            description={tr('All Connections are already linked — or there aren’t any yet. {link}', {
              link: (
                <Link to="/w/$workspaceId/connections" params={{ workspaceId: desk.workspaceId }} className="text-primary hover:underline">
                  {t('Create connection')}
                </Link>
              ),
            })}
          />
        ) : (
          <div className="grid gap-4">
            <Field label={t('Connection')}>
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Choose a Connection')} />
                </SelectTrigger>
                <SelectContent>
                  {addable.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · {CONNECTION_TYPE[c.type]?.label ?? c.type}
                      {c.workspaceId !== desk.workspaceId && ` · ${t('from {workspace}', { workspace: c.workspaceName ?? '…' })}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
<div className="grid gap-2">
              <span className="text-sm font-medium">{t('Permissions')}</span>
              <label className="flex items-start gap-2.5 rounded-md border px-3 py-2.5">
                <Checkbox checked disabled className="mt-0.5" />
                <span className="text-sm">
                  <span className="font-medium">{t('Read')}</span>
                  <span className="block text-xs text-muted-foreground">{t('Agents can fetch data and use read-only tools.')}</span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5">
                <Checkbox checked={write} onChange={(e) => setWrite(e.target.checked)} className="mt-0.5" />
                <span className="text-sm">
                  <span className="font-medium">{t('Write')}</span>
                  <span className="block text-xs text-muted-foreground">
                    {t('Agents can also change data and use tools that make changes. Without Write, Agents can only use read-only tools.')}
                  </span>
                </span>
              </label>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button disabled={!pick} loading={add.isPending} onClick={() => add.mutate()}>
            {t('Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
